import { FC, ReactNode, useContext, useState } from 'react';
import { Page } from './ExamDataGrid.styles';
import { useTranslation } from 'react-i18next';
import {
  LuxGrid,
  Box,
  Typography,
  LuxButton,
  LuxSelectablePill,
  Stack,
} from '@gemini/fe-ds-core';
import ImportDeviceDataContext from '@context/ImportDeviceDataContext';
import AppointmentsContext from '@context/AppointmentsContext';
import OrganizationLocationContext from '@context/OrganizationLocationContext';
import useGetExamData from '@hooks/useGetExamData';
import { getExamImportStatusColor } from '@utils/LuxColorUtil';
import { getExamDataStatusDescription } from '@utils/CodeUtil';
import { format, formatTime } from '@gemini/fe-utils';
import GridHeader from '@components/Grid/GridHeader';
import GridCell from '@components/Grid/GridCell';
import useSubmitExams from '@hooks/useSubmitExams';

interface ExamDataGridProps {
  children?: ReactNode;
}

const ExamDataGrid: FC<ExamDataGridProps> = () => {
  const { t } = useTranslation();
  const [page, setPage] = useState(0);
  const { setModalOpen, newExamOnly, appointment } = useContext(
    ImportDeviceDataContext
  );
  const { date } = useContext(AppointmentsContext);
  const { organizationCoreId, locationCoreId, isSwitchingOrganization } =
    useContext(OrganizationLocationContext);
  const {
    mutate,
    isError,
    isLoading: isSubmitting,
  } = useSubmitExams({ organizationCoreId, locationCoreId, date });
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const {
    data: examData,
    isLoading,
    error,
  } = useGetExamData({
    enabled: !isSwitchingOrganization,
    organizationCoreId: organizationCoreId,
    locationCoreId: locationCoreId,
    date: date,
  });

  return (
    <Page>
      <Box>
        {!error ? (
          <LuxGrid
            id="exam-data-grid"
            cellStyle={{ display: 'flex', alignItems: 'center' }}
            tableHeight={421}
            rowHeight={74}
            rowSelection={{ mode: 'multiRow' }}
            setSelectedCheckboxes={selected =>
              setSelectedRows(selected?.map(row => row.id) || [])
            }
            loading={isLoading}
            luxPagination={{
              sizeOptions: [5],
              current: page,
              totalElements: examData?.length ?? 0,
              selectedSize: 5,
              onSelectPage: p => {
                setPage(p);
              },
              onSizeChange: () => {},
              arrowsPosition: 'left',
              hideNumberPage: true,
              Children: (
                <LuxButton
                  iconStart="DownloadIcon"
                  disabled={
                    selectedRows.length === 0 ||
                    isSubmitting ||
                    isSwitchingOrganization
                  }
                  onClick={() => {
                    const createMutation = (dataPaths: string[]) => {
                      if (dataPaths.length === 0) {
                        return () => setModalOpen(false);
                      }
                      return () =>
                        mutate(
                          {
                            appointmentId: appointment?.id ?? '',
                            dataPath: dataPaths[0],
                          },
                          {
                            onSuccess: () => {
                              createMutation(dataPaths.slice(1))();
                            },
                          }
                        );
                    };
                    createMutation(selectedRows)();
                  }}
                >
                  <Typography variant="body" sx={{ whiteSpace: 'nowrap' }}>
                    {t('examImport.examData.import')}
                  </Typography>
                </LuxButton>
              ),
            }}
            columnDefs={[
              {
                field: 'deviceModel',
                flex: 1,
                cellRenderer: (params: any) => {
                  return (
                    <GridCell>
                      <Typography variant="textMdRegular">
                        {params.data.deviceManufacturer}
                      </Typography>
                      <Typography variant="textXs" color="text.secondary">
                        {params.data.deviceModel}
                      </Typography>
                    </GridCell>
                  );
                },
                headerComponent: () => (
                  <GridHeader
                    header={t('examImport.examData.table.device') ?? ''}
                  />
                ),
              },
              {
                field: 'exameDateTime',
                cellRenderer: (params: any) => {
                  return (
                    <GridCell>
                      <Typography variant="textMdRegular">
                        {format(params.data.exameDateTime)}
                      </Typography>
                      <Typography variant="textMedium">
                        {formatTime(params.data.exameDateTime)}
                      </Typography>
                    </GridCell>
                  );
                },
                headerComponent: () => (
                  <GridHeader
                    header={t('examImport.examData.table.examDate') ?? ''}
                  />
                ),
                flex: 1,
              },
              // {
              //   field: 'fileName',
              //   cellRenderer: () => {
              //     return null;
              //   },
              //   headerComponent: () => (
              //     <GridHeader header={t('examImport.examData.table.summary') ?? ''} />
              //   ),
              //   flex: 1,
              // },
              {
                field: 'importStatus',
                cellRenderer: (params: any) => {
                  return (
                    <GridCell>
                      <LuxSelectablePill
                        selectable={false}
                        color={getExamImportStatusColor(
                          params.data.importStatus
                        )}
                        text={getExamDataStatusDescription(
                          params.data.importStatus
                        )}
                      />
                    </GridCell>
                  );
                },
                flex: 1,
                headerComponent: () => (
                  <GridHeader
                    header={t('examImport.examData.table.status') ?? ''}
                  />
                ),
              },
              {
                field: 'fileName',
                headerName: t('examImport.table.action') ?? '',
                flex: 1,
                cellRenderer: (params: any) => {
                  return (
                    <GridCell>
                      <LuxButton
                        variant="link"
                        iconStart="DownloadIcon"
                        disabled={isSubmitting || isSwitchingOrganization}
                        onClick={() => {
                          mutate(
                            {
                              appointmentId: appointment?.id ?? '',
                              dataPath: params.data.id,
                            },
                            {
                              onSuccess: () => {
                                setModalOpen(false);
                              },
                            }
                          );
                        }}
                      >
                        {t('examImport.examData.table.import')}
                      </LuxButton>
                    </GridCell>
                  );
                },
                headerComponent: () => (
                  <GridHeader
                    header={t('examImport.examData.table.action') ?? ''}
                  />
                ),
              },
            ]}
            rowData={
              examData
                ?.map(e => ({
                  id: `${organizationCoreId}/${locationCoreId}/${e.deviceId}/${e.importStatus}/${e.fileName}`,
                  ...e,
                }))
                .filter(e => (newExamOnly ? e.importStatus === 'new' : true))
                .sort(
                  (a, b) =>
                    new Date(b.exameDateTime).getTime() -
                    new Date(a.exameDateTime).getTime()
                )
                .slice(page * 5, page * 5 + 5) ?? []
            }
          />
        ) : (
          <Typography variant="textMdRegular">
            {t('examImport.examData.table.loadError')}
          </Typography>
        )}
      </Box>
    </Page>
  );
};

export default ExamDataGrid;
